'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import React from 'react';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Button } from '../ui/button';
import { HiArrowRight } from 'react-icons/hi';
import { calculateReadingTime } from '../../utils/calculate-reading-time';

export type BlogCardProps = {
  blog: Blog;
};

export function BlogCard({ blog }: BlogCardProps) {
  const textContent = getTextFromHTML(blog.content);
  const readingTime = calculateReadingTime(textContent);

  const { t } = useTranslations();

  return (
    <Link
      href={ROUTES.BLOG_DETAIL({ slug: blog?.slug }) ?? '#'}
      prefetch={false}
      className="col-span-1 flex w-full flex-col items-start justify-start gap-6"
    >
      <Image
        src={blog?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(blog?.image, blog?.name)}
        width={0}
        height={0}
        className="h-[224px] w-full overflow-hidden rounded-[16px] object-cover object-center sm:h-[270px] lg:h-[320px]"
        sizes="(min-width: 1280px) 427px, (min-width: 768px) 33vw, (min-width: 640px) 50vw, calc(100vw - 32px)"
      />
      <div className="flex flex-col items-start justify-start gap-4">
        <h3
          data-ea-visual-tag="h5"
          className="text-base font-semibold leading-[1.3] tracking-tighter text-foreground sm:text-lg sm:leading-[1.3] md:text-xl md:leading-[1.3] lg:text-[22px] lg:leading-[1.3]"
        >
          {blog.name}
        </h3>
        <div className="flex items-center justify-start gap-2">
          {blog.categories && blog.categories.length > 0 && (
            <>
              <p className="text-sm text-foreground sm:text-base">{blog.categories[0]?.name}</p>
              <span className="text-sm text-[#D3C7B8] sm:text-base">|</span>
            </>
          )}
          <p className="text-sm text-foreground sm:text-base">
            {readingTime} {t('text3360')}
          </p>
        </div>
        <div className="flex items-center justify-start gap-4">
          <Button asChild size={'icon'} rounded={'md'}>
            <span aria-hidden="true"><HiArrowRight size={20} className="text-white" /></span>
          </Button>
          <p className="text-sm font-bold uppercase text-foreground">{t('text1148')}</p>
        </div>
      </div>
    </Link>
  );
}
